// Novedades de cada versión de Veetal.
//
// Cómo añadir una versión nueva en el próximo deploy:
//
//   1. Añade una entrada al principio del array `releases` con la nueva versión.
//   2. Define sus `features`. Cada feature es una tarjeta con captura de pantalla.
//   3. Añade los literales (`title`, `description`, `items`) en es/en/ca/fr.json.
//   4. Deja las capturas en `public/img/versions/<version>/<image>.png`.
//      Si un cliente tiene una pantalla personalizada, deja SU captura en
//      `public/img/versions/<version>/<customer>/<image>.png` y se usará
//      automáticamente para ese customer (ver `public/img/versions/README.md`).
//   5. Actualiza `'version' => 'Veetal vX.Y'` en el backend
//      (hawg-backend: backend/controllers/BaseController.php, actionChainData).
//
// Campos de un feature:
//   id            identificador interno (clave de la lista)
//   icon          icono mdi
//   title         clave i18n del título
//   description   clave i18n de la descripción (admite html)
//   items         array de claves i18n con los puntos concretos
//   image         nombre del png dentro de la carpeta de la versión (sin extensión)
//   page          nombre de ruta a la que enlazar el botón "Ir a la pantalla"
//   visibility    permiso necesario para ver el feature (opcional)
//   visibilities  el feature se ve si el usuario tiene ALGUNO de estos permisos
//   customers     si está definido, sólo se muestra a estos customers
//   variants      customers que tienen captura propia de esa pantalla. Sin esto
//                 NO se busca en la carpeta del customer, para no gastar un 404
//                 por imagen antes de caer a la genérica.
//   tag           clave i18n de la etiqueta (por defecto `release_tag_new`)

export const CURRENT_VERSION = "7.5";

const releases = [
  {
    version: "7.5",
    type: "major",
    date: "2026-08-25",
    title: "release_75_title",
    description: "release_75_description",
    cover: "cover",
    features: [
      {
        id: "pricing_strategy_v2",
        icon: "mdi-strategy",
        title: "release_75_pricing_strategy_title",
        description: "release_75_pricing_strategy_description",
        items: [
          "release_75_pricing_strategy_bullet_1",
          "release_75_pricing_strategy_bullet_2",
          "release_75_pricing_strategy_bullet_3",
          "release_75_pricing_strategy_bullet_4"
        ],
        image: "pricing-strategy-strategies",
        page: "pricing_strategy_strategies",
        visibility: "pricing_strategy",
        tag: "release_tag_new"
      },
      {
        id: "pricing_strategy_templates",
        icon: "mdi-view-grid-plus-outline",
        title: "release_75_rule_templates_title",
        description: "release_75_rule_templates_description",
        items: [
          "release_75_rule_templates_bullet_1",
          "release_75_rule_templates_bullet_2",
          "release_75_rule_templates_bullet_3"
        ],
        image: "pricing-strategy-rule-templates",
        page: "pricing_strategy_strategies",
        visibility: "pricing_strategy",
        tag: "release_tag_new"
      },
      {
        id: "pricing_strategy_guardrails",
        icon: "mdi-shield-check-outline",
        title: "release_75_guardrails_title",
        description: "release_75_guardrails_description",
        items: [
          "release_75_guardrails_bullet_1",
          "release_75_guardrails_bullet_2",
          "release_75_guardrails_bullet_3"
        ],
        image: "pricing-strategy-guardrails",
        page: "pricing_strategy_strategies",
        visibility: "pricing_strategy",
        tag: "release_tag_new"
      },
      {
        id: "pricing_strategy_recommendations",
        icon: "mdi-lightbulb-on-outline",
        title: "release_75_recommendations_title",
        description: "release_75_recommendations_description",
        items: [
          "release_75_recommendations_bullet_1",
          "release_75_recommendations_bullet_2",
          "release_75_recommendations_bullet_3"
        ],
        image: "pricing-strategy-recommendations",
        page: "pricing_strategy_recommendations",
        visibility: "pricing_strategy_suggestions",
        tag: "release_tag_improved"
      },
      {
        id: "alerts",
        icon: "mdi-bell-ring-outline",
        title: "release_75_alerts_title",
        description: "release_75_alerts_description",
        items: [
          "release_75_alerts_bullet_1",
          "release_75_alerts_bullet_2",
          "release_75_alerts_bullet_3"
        ],
        image: "highlights",
        page: "pricing_strategy_recommendations",
        visibility: "pricing_strategy_suggestions",
        tag: "release_tag_new"
      },
      {
        id: "rate_change_log",
        icon: "mdi-history",
        title: "release_75_rate_change_log_title",
        description: "release_75_rate_change_log_description",
        items: [
          "release_75_rate_change_log_bullet_1",
          "release_75_rate_change_log_bullet_2",
          "release_75_rate_change_log_bullet_3",
          "release_75_rate_change_log_bullet_4"
        ],
        image: "rate-change-log",
        page: "rms_rate_change_log",
        visibility: "rms_rate_change_log",
        tag: "release_tag_new"
      },
      {
        id: "bulk_rate_changes",
        icon: "mdi-tag-multiple-outline",
        title: "release_75_bulk_rate_changes_title",
        description: "release_75_bulk_rate_changes_description",
        items: [
          "release_75_bulk_rate_changes_bullet_1",
          "release_75_bulk_rate_changes_bullet_2",
          "release_75_bulk_rate_changes_bullet_3"
        ],
        image: "bulk-rate-changes",
        visibilities: [
          "rms_strategy",
          "rms_strategy_axel",
          "rms_strategy_clicktotravel",
          "rms_strategy_grid"
        ],
        tag: "release_tag_improved"
      },
      {
        id: "rms_strategy",
        icon: "mdi-table-large",
        title: "release_75_rms_strategy_title",
        description: "release_75_rms_strategy_description",
        items: [
          "release_75_rms_strategy_bullet_1",
          "release_75_rms_strategy_bullet_2",
          "release_75_rms_strategy_bullet_3",
          "release_75_rms_strategy_bullet_4"
        ],
        image: "rms-strategy",
        // Customers con captura propia en 7.5/<customer>/rms-strategy.png
        variants: ["clicktotravel"],
        visibilities: [
          "rms_strategy",
          "rms_strategy_axel",
          "rms_strategy_clicktotravel",
          "rms_strategy_grid",
          "rms_strategy_weeks"
        ],
        tag: "release_tag_improved"
      },
      {
        id: "marketsegment",
        icon: "mdi-google-circles-extended",
        title: "release_75_marketsegment_title",
        description: "release_75_marketsegment_description",
        items: [
          "release_75_marketsegment_bullet_1",
          "release_75_marketsegment_bullet_2",
          "release_75_marketsegment_bullet_3"
        ],
        image: "marketsegment",
        // Exclusiu d'Axel
        customers: ["axel"],
        visibility: "rms_marketsegment",
        tag: "release_tag_new"
      },
      {
        id: "sellpar",
        icon: "mdi-cash-multiple",
        title: "release_75_sellpar_title",
        description: "release_75_sellpar_description",
        items: [
          "release_75_sellpar_bullet_1",
          "release_75_sellpar_bullet_2",
          "release_75_sellpar_bullet_3"
        ],
        image: "sellpar",
        // Exclusiu d'Axel
        customers: ["axel"],
        visibility: "rms_strategy_axel",
        tag: "release_tag_new"
      },
      {
        id: "booking_pace",
        icon: "mdi-chart-timeline-variant",
        title: "release_75_booking_pace_title",
        description: "release_75_booking_pace_description",
        items: [
          "release_75_booking_pace_bullet_1",
          "release_75_booking_pace_bullet_2"
        ],
        image: "booking-pace",
        visibility: "dashboard_rms",
        tag: "release_tag_improved"
      },
      {
        id: "rate_graph",
        icon: "mdi-chart-areaspline",
        title: "release_75_rate_graph_title",
        description: "release_75_rate_graph_description",
        items: [
          "release_75_rate_graph_bullet_1",
          "release_75_rate_graph_bullet_2"
        ],
        image: "rate-graph",
        page: "rate",
        visibility: "rate",
        tag: "release_tag_improved"
      },
      {
        id: "ct_kpi_selector",
        icon: "mdi-tune-variant",
        title: "release_75_ct_kpi_selector_title",
        description: "release_75_ct_kpi_selector_description",
        items: [
          "release_75_ct_kpi_selector_bullet_1",
          "release_75_ct_kpi_selector_bullet_2"
        ],
        image: "ct-kpi-selector",
        page: "rms_strategy_clicktotravel",
        visibility: "rms_strategy_clicktotravel",
        tag: "release_tag_new"
      },
      {
        id: "ct_year_sales_spot_date",
        icon: "mdi-calendar-search",
        title: "release_75_ct_year_sales_spot_date_title",
        description: "release_75_ct_year_sales_spot_date_description",
        items: [
          "release_75_ct_year_sales_spot_date_bullet_1",
          "release_75_ct_year_sales_spot_date_bullet_2",
          "release_75_ct_year_sales_spot_date_bullet_3"
        ],
        image: "ct-year-sales-spot-date",
        page: "rms_year_sales_clicktotravel",
        // Exclusiu de Clicktotravel
        customers: ["clicktotravel"],
        // La pantalla es gestiona amb el permís de l'estratègia (mateix criteri
        // que el menú a AppLayout.vue): no hi ha permís propi d'Informe Anual
        visibility: "rms_strategy_clicktotravel",
        tag: "release_tag_new"
      },
      {
        id: "reservation_import_date_format",
        icon: "mdi-calendar-text-outline",
        title: "release_75_import_date_format_title",
        description: "release_75_import_date_format_description",
        items: [
          "release_75_import_date_format_bullet_1",
          "release_75_import_date_format_bullet_2",
          "release_75_import_date_format_bullet_3"
        ],
        image: "reservation-import-date-format",
        page: "revenue-import",
        visibility: "reservations_import_manual",
        tag: "release_tag_new"
      },
      {
        id: "calculated_rates_recalc",
        icon: "mdi-calculator-variant-outline",
        title: "release_75_calculated_rates_recalc_title",
        description: "release_75_calculated_rates_recalc_description",
        items: [
          "release_75_calculated_rates_recalc_bullet_1",
          "release_75_calculated_rates_recalc_bullet_2",
          "release_75_calculated_rates_recalc_bullet_3"
        ],
        image: "calculated-rates-recalc",
        page: "room-types",
        visibility: "roomtypemanagement",
        tag: "release_tag_new"
      }
    ],
    // Cambios menores: se listan sin captura.
    minor_items: [
      "release_75_minor_1",
      "release_75_minor_2",
      "release_75_minor_3",
      "release_75_minor_4"
    ]
  },
  {
    version: "7.0",
    type: "major",
    date: "2026-04-15",
    title: "release_70_title",
    description: "release_70_description",
    minor_items: [
      "release_70_bullet_1",
      "release_70_bullet_2",
      "release_70_bullet_3",
      "release_70_bullet_4",
      "release_70_bullet_5",
      "release_70_bullet_6"
    ]
  },
  {
    version: "6.3",
    type: "major",
    date: "2020-12-15",
    title: "version_63_title",
    description: "version_63_description",
    minor_items: [
      "version_63_bullet_1",
      "version_63_bullet_2",
      "version_63_bullet_3",
      "version_63_bullet_4"
    ]
  },
  {
    version: "6.2",
    type: "major",
    date: "2021-09-15",
    title: "version_62_title",
    description: "version_62_description",
    minor_items: [
      "version_62_bullet_1",
      "version_62_bullet_2",
      "version_62_bullet_3",
      "version_62_bullet_4"
    ]
  },
  {
    version: "6.1",
    type: "major",
    date: "2020-07-10",
    title: "version_61_title",
    description: "version_61_description",
    minor_items: [
      "version_61_bullet_1",
      "version_61_bullet_2",
      "version_61_bullet_3"
    ]
  },
  {
    version: "6.0",
    type: "major",
    date: "2020-05-31",
    title: "version_60_title",
    description: "version_60_description",
    minor_items: [
      "version_60_bullet_1",
      "version_60_bullet_2",
      "version_60_bullet_3",
      "version_60_bullet_4"
    ]
  }
];

export default releases;
